import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ruler, Users, Gauge, ChevronRight } from "lucide-react";
import { supabase } from "../lib/supabase";
import { formatPrice } from "../lib/formatPrice";

interface UsedBoat {
  id: string;
  Title: string;
  Description: string;
  FullInfo: string;
  Price: string;
  BoatLength: string;
  BoatWidth: string;
  BoatCapacity: string;
  BoatWeight: string;
  MinMotorPower?: string;
  MaxMotorPower?: string;
  MotorPower?: string;
  DefaultEquipments: string[];
  ExtraEquipments: string[];
  CatalogImage: string;
  Images: string[];
}

const UsedProducts: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<UsedBoat[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUsedBoats = async () => {
      const { data, error } = await supabase.from('used_boats').select('*');
      if (error) {
        console.error("İkinci el tekneler alınamadı:", error);
      } else {
        setProducts(data as UsedBoat[]);
      }
      setLoading(false);
    };
    fetchUsedBoats();
  }, []);

  const handleClick = (id: string) => {
    navigate(`/ikinciel/${id}`);
  };

  const getImageSource = (boat: UsedBoat): string => {
    if (boat.CatalogImage) {
      return boat.CatalogImage;
    }
    if (boat.Images && boat.Images.length > 0) {
      return boat.Images[0];
    }
    return "/placeholder-boat.jpg"; // Varsayılan görsel
  };

  return (
    <div className="w-full text-black bg-white font-montserrat">
      {/* Başlık */}
      <section className="text-center py-10 px-6 bg-[#002744] text-white">
        <h1 className="text-4xl md:text-6xl font-bold mb-4 tracking-wide">
          İkinci El Tekneler
        </h1>
        <p className="text-lg md:text-xl max-w-3xl mx-auto opacity-80">
          Kaliteli ve uygun fiyatlı ikinci el{" "}
          <span className="font-bold">Tekneler</span> Ayvalık Marin'de sizi
          bekliyor.
        </p>
      </section>

      {/* İçerik */}
      <div className="max-w-7xl mx-auto px-4 py-12 pb-24">
        {loading ? (
          <div className="flex justify-center items-center min-h-[300px]">
            <div className="w-12 h-12 border-4 border-[#009fe3] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[300px] text-center text-gray-600">
            <div className="text-6xl mb-4">⛵</div>
            <h2 className="text-2xl font-semibold mb-2">Henüz tekne eklenmedi</h2>
            <p className="max-w-md">
              İkinci el teknelerimiz çok yakında burada yer alacak. Takipte kalın!
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((boat) => {
              const motorLabel = boat.MotorPower
                ? boat.MotorPower
                : boat.MaxMotorPower
                  ? `${boat.MinMotorPower ? boat.MinMotorPower + '-' : ''}${boat.MaxMotorPower} HP`
                  : boat.MinMotorPower ? `${boat.MinMotorPower} HP` : null;
              const imgSrc = getImageSource(boat);
              const isVideo = imgSrc.endsWith(".mp4");

              return (
                <div
                  key={boat.id}
                  onClick={() => handleClick(boat.id)}
                  className="group cursor-pointer rounded-2xl overflow-hidden flex flex-col bg-white font-montserrat border border-gray-100 shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
                >
                  {/* Image */}
                  <div className="relative w-full bg-gradient-to-br from-gray-50 to-gray-100 aspect-[4/3] overflow-hidden">
                    {isVideo ? (
                      <video
                        src={imgSrc}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        muted
                        preload="metadata"
                      />
                    ) : (
                      <img
                        src={imgSrc}
                        alt={boat.Title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = "/placeholder-boat.jpg";
                        }}
                      />
                    )}
                    <div className="absolute top-3 right-3">
                      <span className="inline-block px-2.5 py-1 bg-amber-500 text-white text-[10px] font-bold tracking-widest uppercase rounded-full shadow-sm">
                        ★ İkinci El
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-5 flex flex-col flex-grow">
                    <h3 className="text-lg font-bold text-[#002744] mb-1 leading-tight">{boat.Title}</h3>
                    <p className="text-sm text-gray-500 mb-4 line-clamp-2 min-h-[40px]">
                      {boat.Description}
                    </p>

                    {/* Spec mini-grid */}
                    <div className="grid grid-cols-3 gap-2 mb-4 pb-4 border-b border-gray-100">
                      <div className="flex flex-col items-center text-center">
                        <Ruler size={16} className="text-[#009fe3] mb-1" />
                        <span className="text-[10px] text-gray-400 uppercase tracking-wider">Uzunluk</span>
                        <span className="text-xs font-bold text-gray-800">{boat.BoatLength ? `${boat.BoatLength}m` : '-'}</span>
                      </div>
                      <div className="flex flex-col items-center text-center border-x border-gray-100">
                        <Users size={16} className="text-[#009fe3] mb-1" />
                        <span className="text-[10px] text-gray-400 uppercase tracking-wider">Kapasite</span>
                        <span className="text-xs font-bold text-gray-800">{boat.BoatCapacity ? `${boat.BoatCapacity} Kişi` : '-'}</span>
                      </div>
                      <div className="flex flex-col items-center text-center">
                        <Gauge size={16} className="text-[#009fe3] mb-1" />
                        <span className="text-[10px] text-gray-400 uppercase tracking-wider">Motor</span>
                        <span className="text-xs font-bold text-gray-800">{motorLabel || '-'}</span>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="mt-auto">
                      {boat.Price ? (
                        <>
                          <div className="text-[10px] text-gray-400 uppercase tracking-widest mb-0.5">Fiyat</div>
                          <div className="text-2xl font-bold text-[#002744]">
                            {formatPrice(boat.Price)} <span className="text-base font-normal text-gray-400">₺</span>
                          </div>
                        </>
                      ) : (
                        <div className="text-sm font-semibold text-gray-500 italic">Fiyat için iletişime geçin</div>
                      )}

                      <button
                        type="button"
                        className="mt-4 w-full px-4 py-2.5 bg-[#002744] hover:bg-[#003a6b] text-white text-sm font-semibold rounded-lg flex items-center justify-center gap-2 group-hover:bg-[#009fe3] transition-colors"
                      >
                        DETAYLI BİLGİ
                        <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default UsedProducts;